import axios from '@/zenky'
import config from '@/config'
import storage from '@/storage'
import get from 'lodash/get'
import {getErrResponseData, getTranslatedErrorMessage} from "@/helpers/apiErrors";

let actionsCheckerTimeout = 0

export default {
  namespaced: true,

  state: {
    order: {},

    maxBonusesPayment: null,

    draftPayments: {
      // We only send the bonuses payment draft so we can easily determine how much there is left to pay with real
      // money, by accessing the `paymentsPreview.unpaid`.
      bonuses: {
        amount: 0,
      },
    },

    paymentsPreview: null,
    paymentsPreviewIsLoading: false,

    rewardSelector: {
      available: false,
      promotion_id: '',
      reward_id: '',
      max_quantity: 0,
      context: null,
    },

    rewards: [],

    rewardsAreReloading: false,
    promotionsAreChecking: false,
    checkoutUrlIsGenerating: false,
  },

  getters: {
    hasOrder(state) {
      return !!state.order && !!state.order.id
    },

    hasVariants(state, getters) {
      if (!getters.hasOrder) {
        return false
      }

      return getters.order.variants.length > 0
    },

    order(state) {
      return state.order
    },

    orderId(state, getters) {
      if (!getters.hasOrder) {
        return ''
      }

      return getters.order.id
    },

    orderToken(state, getters) {
      if (!getters.hasOrder) {
        return ''
      }

      return getters.order.token
    },

    variants(state, getters) {
      if (!getters.hasVariants) {
        return []
      }

      return state.order.variants
    },

    totalQuantity(state, getters) {
      if (!getters.hasVariants) {
        return 0
      }

      return state.order.variants
        .map(v => v.quantity)
        .reduce((prev, current) => prev + current)
    },

    totalPrice(state, getters) {
      if (!getters.hasOrder) {
        return null
      }

      return state.order.total_price
    },

    draftPayments2Api(state) {
      const payments = [];
      if (state.draftPayments.bonuses.amount > 0) {
        payments.push({
          method: 'bonuses',
          amount: state.draftPayments.bonuses.amount,
        });
      }
      return payments;
    },

    maxBonusesPayment(state) {
      if (!state.paymentsPreview || !state.paymentsPreview.max_bonuses_payment) {
        return null
      }

      return state.paymentsPreview.max_bonuses_payment
    },

    paymentsPreviewIsLoading(state) {
      return state.paymentsPreviewIsLoading
    },

    rewardSelector(state) {
      return state.rewardSelector
    },

    rewards(state) {
      return state.rewards
    },

    rewardsAreReloading(state) {
      return state.rewardsAreReloading
    },

    promotionsAreChecking(state) {
      return state.promotionsAreChecking
    },

    checkoutUrlIsGenerating(state) {
      return state.checkoutUrlIsGenerating;
    },
  },

  mutations: {
    setOrder(state, order) {
      state.order = order
    },

    setBonusesAmount(state, newVal) {
      state.draftPayments.bonuses.amount = newVal;
    },

    setPaymentsPreviewLoading(state, value) {
      state.paymentsPreviewIsLoading = value
    },

    setPaymentsPreview(state, newVal) {
      state.paymentsPreview = newVal;
    },

    setRewardSelector(state, selector) {
      state.rewardSelector.available = selector.available
      state.rewardSelector.promotion_id = selector.promotion_id
      state.rewardSelector.reward_id = selector.reward_id
      state.rewardSelector.max_quantity = selector.max_quantity
      state.rewardSelector.context = selector.context
    },

    disableRewardSelector(state) {
      state.rewardSelector.available = false
      state.rewardSelector.promotion_id = ''
      state.rewardSelector.reward_id = ''
      state.rewardSelector.max_quantity = 0
      state.rewardSelector.context = null
    },

    setRewards(state, rewards) {
      state.rewards = rewards
    },

    setRewardsAreReloading(state, value) {
      state.rewardsAreReloading = value
    },

    setPromotionsAreChecking(state, value) {
      state.promotionsAreChecking = value
    },

    setCheckoutUrlIsGenerating(state, value) {
      state.checkoutUrlIsGenerating = value;
    },

    setOrderPromocode(state, promocode) {
      state.order = {
        ...state.order,
        promocode,
      };
    },
  },

  actions: {
    async initOrder({ dispatch }) {
      const orderId = storage.activeOrderId()
      const orderToken = storage.activeOrderToken()

      if (!orderId || !orderToken) {
        return dispatch('createOrder');
      } else {
        return dispatch('loadOrder', { orderId, orderToken })
      }
    },

    async recreateOrder({ commit, dispatch }) {
      commit('setOrder', {})
      storage.activeOrderId(null, true)
      storage.activeOrderToken(null, true)
      return dispatch('initOrder');
    },

    async repeatOrder({ dispatch, getters }, orderId) {
      try {
        const response = (await axios.post(`orders/${orderId}/repeat`, {
          current_order_id: getters.orderId,
        })).data.data;

        await dispatch('loadOrder', { orderId: response.id, orderToken: response.token });

        return true;
      } catch (e) {
        const error = getErrResponseData(e)
        const errorMessage = error.error.message || error.error.original_message

        dispatch('notifications/addNotification', {
          id: Math.random().toString().replace('.', ''),
          type: 'error',
          title: 'Не удалось повторить заказ',
          description: errorMessage || 'Произошла неизвестная ошибка.',
          timeout: 5000,
        }, { root: true });

        storage.repeatedFromTabId(null, true);

        return false;
      }
    },

    async cancelOrder({ dispatch, getters }, orderId) {
      try {
        await axios.post(`orders/${orderId}/cancel`);

        return true;
      } catch (e) {
        const error = getErrResponseData(e);
        const errorMessage = error.error.message || error.error.original_message;

        dispatch('notifications/addNotification', {
          id: Math.random().toString().replace('.', ''),
          type: 'error',
          title: 'Не удалось отменить заказ',
          description: errorMessage || 'Произошла неизвестная ошибка.',
          timeout: 5000,
        }, { root: true });

        return false;
      }
    },

    async loadOrder({ dispatch, commit }, { orderId, orderToken }) {
      try {
        const orderReq = axios.get(`orders/${orderId}`, {
          params: {
            token: orderToken,
            with: config.includes.order,
          }
        });

        const order = (await orderReq).data.data;

        if (order?.current_status?.status?.kind?.id !== 'pending') {
          return dispatch('createOrder');
        }

        commit('setOrder', order);
        dispatch('saveOrderCredentials', order);
        dispatch('listenForOrderEvents', order)
        dispatch('loadPromotionRewards')

        return order;
      } catch (e) {
        return dispatch('createOrder')
      }
    },

    setBonusesAmount({ commit }, newVal) {
      commit('setBonusesAmount', newVal);
    },

    async loadPaymentsPreview({ commit, getters }) {
      commit('setPaymentsPreviewLoading', true)

      const res = await axios.post(`/orders/${getters.orderId}/payments/preview`, {
        payments: getters.draftPayments2Api,
        token: getters.orderToken,
      });

      const newVal = res.data.data;

      commit('setPaymentsPreview', newVal);
      commit('setPaymentsPreviewLoading', false)

      return newVal;
    },

    async loadPaymentMethods({ getters }) {
      const response = await axios.get(`orders/${getters.orderId}/payment-methods?token=${getters.orderToken}`);

      return response.data.data;
    },

    async loadDeliveryMethods({ getters }) {
      const response = await axios.get(`/orders/${getters.orderId}/delivery-methods?token=${getters.orderToken}`);

      return response.data.data;
    },

    async createOrder({ dispatch }) {
      const order = (await axios.post('orders', {
        city_id: storage.activeCityId(),
      })).data.data;

      return dispatch('loadOrder', { orderId: order.id, orderToken: order.token });
    },

    saveOrderCredentials(context, order) {
      storage.activeOrderId(order.id)
      storage.activeOrderToken(order.token)
    },

    listenForOrderEvents({ dispatch }, order) {
      return dispatch('broadcast/connect', order, { root: true })
        .then(() => dispatch('checkPromotionActions'))
        .catch(err => console.log('[Order/listenForOrderEvents] Got rejection from broadcast module.', err))
    },

    async addProductVariant({ dispatch, getters }, payload) {
      if (!getters.hasOrder) {
        throw new Error('Order is not available yet.')
      }

      try {
        const order = (await axios.post(`orders/${getters.order.id}/products`, {
          token: getters.order.token,
          product_variant_id: payload.variantId,
          quantity: parseInt(get(payload, 'quantity', 1), 10),
          with: config.includes.orderProduct,
          modifiers: get(payload, 'modifiers'),
          promotion_id: get(payload, 'promotion_id'),
          promotion_reward_id: get(payload, 'promotion_reward_id'),
          stock_id: get(payload, 'stock_id'),
        })).data.data;

        await dispatch('updateOrderData', order);

        dispatch('checkPromotionActions')
      } catch (e) {
        const error = getErrResponseData(e)
        const errorMessage = error.error.message || error.error.original_message

        if (errorMessage) {
          dispatch('notifications/addNotification', {
            id: Math.random().toString().replace('.', ''),
            type: 'error',
            title: 'Не удалось добавить товар',
            description: errorMessage,
            timeout: 5000,
          }, { root: true})
        }

        throw e
      }

      return getters.order;
    },

    async removeProductVariant({ dispatch, getters }, payload) {
      if (!getters.hasOrder) {
        throw new Error('Order is not available yet.')
      }

      try {
        const order = (await axios.post(`orders/${getters.order.id}/products/remove`, {
          token: getters.order.token,
          product_variant_id: payload.variantId,
          quantity: parseInt(get(payload, 'quantity', 1), 10),
          with: config.includes.orderProduct,
          modifiers: get(payload, 'modifiers'),
          promotion_id: get(payload, 'promotion_id'),
          promotion_reward_id: get(payload, 'promotion_reward_id'),
          stock_id: get(payload, 'stock_id'),
        })).data.data;

        await dispatch('updateOrderData', order);

        dispatch('checkPromotionActions')
      } catch (e) {
        const error = getErrResponseData(e)
        const errorMessage = error.error.message || error.error.original_message

        if (errorMessage) {
          dispatch('notifications/addNotification', {
            id: Math.random().toString().replace('.', ''),
            type: 'error',
            title: 'Не удалось удалить товар',
            description: errorMessage,
            timeout: 5000,
          }, { root: true})
        }

        throw e
      }

      return getters.order;
    },

    async updateOrderData({ commit, getters }, order) {
      const func = (await import('@/helpers/order')).updateOrderData;
      commit('setOrder', func(getters.order, order))
    },

    async submitOrder({ getters }, form) {
      if (!getters.hasOrder) {
        throw new Error('Order is not available yet.');
      }

      form.token = getters.order.token

      return (await axios.post(`orders/${getters.order.id}/submit`, form)).data.data;
    },

    async updateOrder({ getters }, form) {
      if (!getters.hasOrder) {
        throw new Error('Order is not available yet.');
      }

      form.token = getters.order.token

      return (await axios.put(`orders/${getters.order.id}`, form)).data.data;
    },

    async createPaymentTransaction({ getters }, form) {
      if (!getters.hasOrder) {
        throw new Error('Order is not available yet.');
      }

      form.token = getters.order.token

      return (await axios.post(`orders/${getters.order.id}/payments`, form)).data.data;
    },

    async confirmOrder({ getters }, code) {
      if (!getters.hasOrder) {
        throw new Error('Order is not available yet.');
      }

      const res = await axios.post(`orders/${getters.order.id}/confirm`, { code });
      const data = res.data.data;
      if (!data.success) {
        throw new Error(res);
      }
      return data;
    },

    async checkPromotionActions({ getters, commit, rootGetters }) {
      if (!getters.hasOrder) {
        throw new Error('Order is not available yet.')
      } else if (!rootGetters['store/loyaltyEnabled']) {
        return
      } else if (!rootGetters['broadcast/available']) {
        return
      }

      clearTimeout(actionsCheckerTimeout)

      actionsCheckerTimeout = setTimeout(async () => {
        const result = (await axios.post(`orders/${getters.order.id}/loyalty/promotions/check?token=${getters.order.token}`)).data.data

        console.log(`[order/checkPromotionActions] Actions checker was${result ? '' : ' not'} dispatched.`)

        if (result) {
          commit('setPromotionsAreChecking', true)
          commit('disableRewardSelector')
          setTimeout(() => commit('setPromotionsAreChecking', false), 10000)
        }
      }, 2000)
    },

    async generateCheckoutUrl({ getters, commit, rootGetters }, payload = {}) {
      if (getters.checkoutUrlIsGenerating) {
        return null;
      }

      commit('setCheckoutUrlIsGenerating', true);

      try {
        const data = {
          redirect_url: location.href,
          meta: {
            yandex_metrika: {
              id: rootGetters['settings/yandexMetrika']?.id || null,
              ecommerce: rootGetters['settings/yandexMetrika']?.config?.ecommerce,
            }
          },
        };

        if (payload?.commerceEventsIdentifiers?.anonymous_id && payload?.commerceEventsIdentifiers?.session_id) {
          data.anonymous_id = payload.commerceEventsIdentifiers.anonymous_id;
          data.session_id = payload.commerceEventsIdentifiers.session_id;
        }

        const response = (await axios.post(`orders/${getters.order.id}/checkout/url?token=${getters.order.token}`, data)).data.data;

        return response.url || null;
      } catch (e) {
        commit('setCheckoutUrlIsGenerating', false);
        throw e;
      } finally {
        commit('setCheckoutUrlIsGenerating', false);
      }

      return null;
    },

    async loadPromotionRewards({ getters, commit, rootGetters }) {
      if (!getters.hasOrder) {
        throw new Error('Order is not available yet.')
      } else if (!rootGetters['store/loyaltyEnabled']) {
        return
      }

      const result = (await axios.get(`orders/${getters.order.id}/loyalty/promotions/rewards?token=${getters.order.token}`)).data.data

      commit('setRewards', result)
    },

    async applyPromocode({ commit, dispatch, getters }, { promocode }) {
      try {
        await axios.post(`orders/${getters.orderId}/checkout/promocode?token=${getters.order.token}`, { promocode });

        await dispatch('loadOrder', { orderId: getters.orderId, orderToken: getters.order.token });
        await dispatch('checkPromotionActions');

        return true;
      } catch (e) {
        const error = getErrResponseData(e);
        const errorMessage = error.error.message || error.error.original_message;

        dispatch('notifications/addNotification', {
          id: Math.random().toString().replace('.', ''),
          type: 'error',
          title: 'Не удалось применить промокод',
          description: errorMessage || 'Произошла неизвестная ошибка.',
          timeout: 5000,
        }, { root: true });

        return false;
      }
    },

    async cancelPromocode({ dispatch, getters }) {
      try {
        await axios.delete(`orders/${getters.orderId}/checkout/promocode?token=${getters.order.token}`);
        await dispatch('loadOrder', { orderId: getters.orderId, orderToken: getters.order.token });
        await dispatch('checkPromotionActions');

        return true;
      } catch (e) {
        const error = getErrResponseData(e);
        const errorMessage = error.error.message || error.error.original_message;

        dispatch('notifications/addNotification', {
          id: Math.random().toString().replace('.', ''),
          type: 'error',
          title: 'Не удалось удалить промокод',
          description: errorMessage || 'Произошла неизвестная ошибка.',
          timeout: 5000,
        }, { root: true });

        return false;
      }
    },
  },
}
